<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <expansion-card
      icon="mdi-map-marker"
      class="pb-1"
    >
      <span slot="title">Location</span>
      <v-flex class="pa-3">
        <v-text-field
          v-model.trim="itemData.name"
          :label="type + ' Name'"
          :rules="rules().required('Name')"
          :error-messages="getPathErrors(jsonPath, 'name')"
          :disabled="isFormDisabled"
          required
          @input="clearPathErrors(jsonPath, 'name')"
        />

        <v-textarea
          v-model.trim="itemData.notes"
          label="Comments"
          :rows="1"
          auto-grow
          :error-messages="getPathErrors(jsonPath, 'notes')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'name')"
        />

        <v-boolean
          v-model="itemData.eventOnly"
          label="Use For Events Only"
          :error-messages="getPathErrors(jsonPath, 'eventOnly')"
          :disabled="isFormDisabled"
        />

        <v-autocomplete
          v-if="!itemData.eventOnly"
          v-model="itemData.kitchenId"
          label="Kitchen"
          placeholder="Select Kitchen"
          :items="kitchens"
          :rules="kitchenIdRules()"
          :required="!itemData.eventOnly"
          :clearable="itemData.eventOnly"
          auto
          :error-messages="getPathErrors(jsonPath, 'kitchenId')"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @input="clearPathErrors(jsonPath, 'kitchenId')"
          @click:clear="$nextTick(() => itemData.kitchenId = null)"
        />
      </v-flex>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card
      icon="mdi-counter"
      class="pb-1"
    >
      <span slot="title">Min / Max</span>

      <v-layout
        v-if="!itemData.eventOnly"
        wrap
      >
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model="itemData.maxPartySize"
            label="Default Maximum Party Size"
            :rules="rules().required()"
            type="number"
            required
            :error-messages="getPathErrors(jsonPath, 'maxPartySize')"
            :disabled="isFormDisabled"
            @blur="itemData.maxPartySize = forceInt(itemData.maxPartySize)"
            @input="clearPathErrors(jsonPath, 'maxPartySize')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model="itemData.maxPartySizeStaff"
            label="Default Maximum Staff Party Size"
            :rules="rules().required()"
            type="number"
            required
            :error-messages="getPathErrors(jsonPath, 'maxPartySizeStaff')"
            :disabled="isFormDisabled"
            @blur="itemData.maxPartySizeStaff = forceInt(itemData.maxPartySizeStaff)"
            @input="clearPathErrors(jsonPath, 'maxPartySizeStaff')"
          />
        </v-flex>
      </v-layout>

      <v-text-field
        v-if="!itemData.eventOnly"
        v-model="itemData.defaultCoversPerQtrHour"
        label="Default Maximum Covers Per Quarter Hour"
        class="mx-3"
        :rules="rules().required()"
        type="number"
        required
        :error-messages="getPathErrors(jsonPath, 'defaultCoversPerQtrHour')"
        :disabled="isFormDisabled"
        @blur="itemData.defaultCoversPerQtrHour = forceInt(itemData.defaultCoversPerQtrHour)"
        @input="clearPathErrors(jsonPath, 'defaultCoversPerQtrHour')"
      />

      <v-text-field
        v-if="!itemData.eventOnly"
        v-model="itemData.defaultMemberCoversPerQtrHour"
        label="Default Maximum Member Made Covers Per Quarter Hour"
        class="mx-3"
        :rules="rules().required()"
        type="number"
        required
        :error-messages="getPathErrors(jsonPath, 'defaultMemberCoversPerQtrHour')"
        :disabled="isFormDisabled"
        @blur="itemData.defaultMemberCoversPerQtrHour = forceInt(itemData.defaultMemberCoversPerQtrHour)"
        @input="clearPathErrors(jsonPath, 'defaultMemberCoversPerQtrHour')"
      />

      <v-text-field
        v-if="!itemData.eventOnly"
        v-model="itemData.minAdvanceDays"
        label="Minimum Days in Advance"
        class="mx-3"
        :rules="rules().required()"
        type="number"
        required
        :error-messages="getPathErrors(jsonPath, 'minAdvanceDays')"
        :disabled="isFormDisabled"
        @blur="itemData.minAdvanceDays = forceInt(itemData.minAdvanceDays)"
        @input="clearPathErrors(jsonPath, 'minAdvanceDays')"
      />

      <v-text-field
        v-if="!itemData.eventOnly"
        v-model="itemData.maxAdvanceDays"
        label="Maximum Days in Advance"
        class="mx-3"
        :rules="rules().required()"
        type="number"
        required
        :error-messages="getPathErrors(jsonPath, 'maxAdvanceDays')"
        :disabled="isFormDisabled"
        @blur="itemData.maxAdvanceDays = forceInt(itemData.maxAdvanceDays)"
        @input="clearPathErrors(jsonPath, 'maxAdvanceDays')"
      />
    </expansion-card>

    <v-divider class="my-3" />

    <detail-editor-table
      v-model="itemData.tables"
      type="Table"
      :disabled="isFormDisabled"
      :sortable="!alphaSortLocationTables"
      :json-path="buildJsonPath('tables')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :item-default="{ name: null, quantity: null, locationId: itemData.id, pos: 0, disabled: false }"
      :headers="[
        ...(alphaSortLocationTables ? [] : [headerDragSort()]),
        headerEditor({label: 'Table', field: 'name', editor: {required: true, rules: rules().required()}}),
        headerEditor({label: 'Quantity', field: 'quantity', editor: {required: true, type: 'int'}, thin: true}),
        headerEditor({label: 'Min Covers', field: 'minCovers', editor: {required: true, type: 'int'}, thin: true}),
        headerEditor({label: 'Max Covers', field: 'maxCovers', editor: {required: true, type: 'int'}, thin: true}),
        headerEditor({label: 'Max Join Covers', field: 'maxJoinCovers', editor: {required: true, type: 'int'}, thin: true}),
        headerEditor({label: 'Enabled', field: 'disabled', editor: {type: 'switch', invert: true}, thin: true})
      ]"
      @error="addError"
      @add="resetItemFieldErrorLookup"
    />

    <v-divider class="my-3" />

    <expansion-card
      icon="mdi-cog"
      class="pb-1"
    >
      <span slot="title">Options</span>

      <v-flex class="pa-3">
        <v-boolean
          v-model="itemData.requestTableNumber"
          label="Request Table Number"
          :error-messages="getPathErrors(jsonPath, 'requestTableNumber')"
          :field-errors="getPathErrors(jsonPath, 'requestTableNumber')"
          :disabled="isFormDisabled"
        />
        <!-- This should be controlled in meal period
        <v-boolean
          v-if="!itemData.eventOnly"
          label="Allow Members to Make Reservations"
          v-model="itemData.allowMemberReservations"
          :error-messages="getPathErrors(jsonPath, 'allowMemberReservations')"
          :field-errors="getPathErrors(jsonPath, 'allowMemberReservations')"
          :disabled="isFormDisabled"
        ></v-boolean>
  -->
        <v-boolean
          v-model="itemData.disabled"
          label="Enabled"
          invert
          :error-messages="getPathErrors(jsonPath, 'disabled')"
          :field-errors="getPathErrors(jsonPath, 'disabled')"
          :disabled="isFormDisabled"
        />
      </v-flex>
    </expansion-card>
    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {itemEdit, withChildren, DetailEditorTable} from '@/ft-vue-core'

var itemDefault = {
  name: '',
  notes: '',
  kitchenId: null,
  eventOnly: false,
  requestTableNumber: false,
  allowMemberReservations: true,
  maxPartySize: null,
  maxPartySizeStaff: null,
  defaultCoversPerQtrHour: null,
  defaultMemberCoversPerQtrHour: null,
  minAdvanceDays: null,
  maxAdvanceDays: null,
  disabled: false,
  id: null,
  recordLockExpiration: null,
  tables: []
}

export default {
  name: 'LocationsItem',
  components: {
    DetailEditorTable
  },
  mixins: [itemEdit, withChildren],
  data: function () {
    return {
      type: 'Location',
      types: 'Locations',
      apiRecordCommand: 'diningLocation',
      itemDefault: itemDefault,
      kitchens: []
    }
  },
  computed: {
    alphaSortLocationTables () {
      return !!this.$ftCS.DINING_TABLE_ALPHA_SORT
    }
  },
  methods: {
    kitchenIdRules () {
      return [(v) => {return this.itemData.eventOnly || !!v || 'Kitchen Required'}]
    },
    itemResourceCommands () {
      return [
        {command: 'diningKitchen', load: this.kitchens, loading: this.isLoading}
      ]
    }
  }
}
</script>
